import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { createReducer, on, Action } from '@ngrx/store'

import * as UserAccountsActions from './user-accounts.actions'
import { UserAccountsEntity, userAccountsEntityName } from './user-accounts.models'
import { initialUsersState } from '@galabid/gb-dashboard/user/data-access'
import {
  getAddSearchHistoryAction,
  clearSearchHistoryAction,
} from 'libs/gb-dashboard/shared/data-access-search-history/src/lib/+state/search-history.actions'
export const USER_ACCOUNTS_FEATURE_KEY = 'userAccounts'

export interface UserAccountsState extends EntityState<UserAccountsEntity> {
  selectedId?: string | number
  loaded: boolean
  saving: boolean
  count: number
  searchHistory: string[]
}

export interface UserAccountsPartialState {
  readonly [USER_ACCOUNTS_FEATURE_KEY]: UserAccountsState
}

export const userAccountsAdapter: EntityAdapter<UserAccountsEntity> = createEntityAdapter<UserAccountsEntity>({
  selectId: (userAccount: UserAccountsEntity) => userAccount.uuid,
})

export const initialUserAccountsState: UserAccountsState = userAccountsAdapter.getInitialState({
  loaded: true,
  saving: false,
  searchHistory: [],
  count: 0,
})

const reducer = createReducer(
  initialUserAccountsState,
  on(UserAccountsActions.loadUserAccounts, (state) => ({
    ...state,
    loaded: false,
    saving: false,
    selectedId: undefined,
  })),
  on(UserAccountsActions.setUserAccounts, (state, action) =>
    userAccountsAdapter.setAll(action.userAccounts, { ...state, loaded: true, count: action.count }),
  ),
  on(UserAccountsActions.loadUserAccountsSuccess, (state, action) =>
    userAccountsAdapter.upsertMany(action.userAccounts, { ...state, loaded: true, count: action.count }),
  ),
  on(UserAccountsActions.openArchiveUserDialog, (state, action) => ({
    ...state,
    // set selected user on dialog open
    selectedId: action.uuid,
  })),
  on(UserAccountsActions.archiveUser, (state) => ({ ...state, loaded: false, saving: true })),
  on(UserAccountsActions.loadUserAccountsFailure, UserAccountsActions.archiveUserFailure, (state, action) => ({
    ...state,
    loaded: true,
    saving: false,
  })),
  on(UserAccountsActions.removeFromUnsubscribeListFailure, (state) => ({ ...state, saving: false })),
  on(UserAccountsActions.removeFromUnsubscribeListSuccess, (state, action) =>
    userAccountsAdapter.updateOne(
      { changes: { is_email_unsubscribed: false }, id: action.uuid },
      { ...state, saving: false },
    ),
  ),
  on(UserAccountsActions.archiveUserSuccess, (state, action) =>
    userAccountsAdapter.updateOne(
      { changes: action.userAccount, id: action.userAccount.uuid },
      { ...state, loaded: true, saving: false },
    ),
  ),
  on(getAddSearchHistoryAction(userAccountsEntityName), (state, action) => ({
    ...state,
    searchHistory: [...state.searchHistory, action.searchTerm],
  })),
  on(clearSearchHistoryAction(userAccountsEntityName), (state, action) => ({
    ...state,
    searchHistory: initialUsersState.searchHistory,
  })),
  on(UserAccountsActions.clearSelectedId, (state) => ({
    ...state,
    selectedId: undefined,
  })),
)

export function userAccountsReducer(state: UserAccountsState | undefined, action: Action) {
  return reducer(state, action)
}
